import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Brain, 
  Shield, 
  TrendingUp, 
  Users, 
  FileCheck, 
  Video, 
  Calendar, 
  Globe,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Heart,
  ExternalLink
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const Homepage = () => {
  const { t, isRTL } = useLanguage();
  
  const features = [
    {
      icon: Brain,
      title: t('home.aiAssistedTitle'),
      description: t('home.aiAssistedDesc')
    },
    {
      icon: FileCheck,
      title: 'Structured Document Collection',
      description: 'Organised document collection and review workflows designed to support credential checks and compliance processes.'
    },
    {
      icon: Video,
      title: 'Video Interview Platform',
      description: 'Candidates complete short video interviews answering role-related questions to support initial screening and employer review.'
    },
    {
      icon: Calendar,
      title: 'Smart Scheduling',
      description: 'Flexible interview scheduling that coordinates between candidates and employers through the platform.'
    },
    {
      icon: Shield,
      title: 'SCFHS Guidance',
      description: 'Information and guidance to support candidates through Saudi licensing requirements and SCFHS registration.'
    },
    {
      icon: Globe,
      title: 'Relocation Information',
      description: 'Resources and guidance for visa processing, travel arrangements, and settling in Saudi Arabia.'
    }
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : ''}`}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#42b1c2]/5 via-white to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className={`inline-flex items-center space-x-2 bg-[#edd982]/20 text-gray-800 px-4 py-2 rounded-full mb-6 ${isRTL ? 'space-x-reverse' : ''}`}>
              <Sparkles className="h-4 w-4 text-[#42b1c2]" />
              <span className="text-sm font-medium">{t('home.platformBadge')}</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              {t('home.heroTitle')}{' '}
              <span className="bg-gradient-to-r from-[#42b1c2] to-[#3a9fb0] bg-clip-text text-transparent">
                {t('home.heroTitleHighlight')}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              {t('home.heroSubtitle')}
            </p>
            
            <p className="text-lg text-gray-500 mb-12 max-w-3xl mx-auto">
              {t('home.heroDescription')}
            </p>
            
            <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <Link to="/join-talent-pool">
                <Button size="lg" className={`bg-[#42b1c2] hover:bg-[#3a9fb0] text-white px-8 py-6 text-lg group ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {t('home.forMedicalProfessionals')}
                  <ArrowRight className={`h-5 w-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
              <Link to="/register/employer">
                <Button size="lg" variant="outline" className={`border-[#42b1c2] text-[#42b1c2] hover:bg-[#42b1c2] hover:text-white px-8 py-6 text-lg group ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {t('home.forHospitalsEmployers')}
                  <ArrowRight className={`h-5 w-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Designed to Streamline Healthcare Recruitment
            </h2>
            <p className="text-lg text-gray-600">
              Built with transparency, safety, and long-term healthcare workforce quality in mind.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Platform Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tools and workflows designed to support efficient and transparent healthcare recruitment.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-[#42b1c2] transition-all hover:shadow-lg group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#42b1c2] transition-colors">
                    <feature.icon className="h-6 w-6 text-[#42b1c2] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Founder-Led Feature Highlight */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-6">
            <Heart className="h-4 w-4" />
            <span className="text-sm font-semibold">Healthcare-Led Platform</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Founded by a Medical Professional
          </h2>
          <p className="text-xl mb-8 text-white/90 max-w-3xl mx-auto">
            With over 15 years of clinical practice and healthcare leadership experience, 
            Luma Med Global is built with a deep understanding of healthcare systems, regulation, and workforce needs.
          </p>
          <Link to="/medically-led-advantage">
            <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-lg group">
              Learn About Our Approach
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Vision 2030 Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#42b1c2]/10 via-[#edd982]/5 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Supporting Saudi Vision 2030
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Aligned with the Kingdom&apos;s healthcare transformation goals, Luma Med Global aims to:
              </p>
              <ul className="space-y-4">
                {[
                  'Support the attraction of international medical talent to Saudi Arabia',
                  'Provide tools to improve healthcare recruitment efficiency',
                  'Leverage technology to support healthcare workforce development',
                  'Contribute to the Kingdom\'s healthcare transformation journey'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="text-6xl font-bold text-[#42b1c2] mb-4">2030</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Healthcare Transformation
              </h3>
              <p className="text-gray-600">
                We aim to contribute to Saudi Arabia&apos;s vision of developing a world-class healthcare sector 
                by connecting qualified medical professionals with opportunities across the Kingdom.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - Replacing Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Commitment
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Built with transparency, safety, and long-term healthcare workforce quality in mind.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-teal-200 bg-teal-50">
                <CardContent className="p-6 text-center">
                  <Shield className="h-10 w-10 text-teal-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">Transparent Process</h3>
                  <p className="text-sm text-gray-600">Clear, honest communication at every step</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="p-6 text-center">
                  <Users className="h-10 w-10 text-blue-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">Human-Centred</h3>
                  <p className="text-sm text-gray-600">People are at the heart of everything we do</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="h-10 w-10 text-purple-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">Quality Focus</h3>
                  <p className="text-sm text-gray-600">Committed to long-term healthcare workforce quality</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Employer Value Proposition Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#42b1c2]/10 text-[#42b1c2] text-sm font-medium mb-6">
                For Healthcare Employers
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Luma Med Global
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                A structured approach to international healthcare recruitment, designed to support efficient hiring while maintaining compliance with regulatory requirements.
              </p>
              
              {/* Benefits List */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#42b1c2]/10 flex items-center justify-center mt-0.5">
                    <CheckCircle className="h-4 w-4 text-[#42b1c2]" />
                  </div>
                  <span className="text-gray-900">Hiring timelines typically start from 21 days</span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#42b1c2]/10 flex items-center justify-center mt-0.5">
                    <CheckCircle className="h-4 w-4 text-[#42b1c2]" />
                  </div>
                  <span className="text-gray-900">AI-powered screening and verification</span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#42b1c2]/10 flex items-center justify-center mt-0.5">
                    <CheckCircle className="h-4 w-4 text-[#42b1c2]" />
                  </div>
                  <span className="text-gray-900">Transparent pricing — subscription or per-hire model</span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#42b1c2]/10 flex items-center justify-center mt-0.5">
                    <CheckCircle className="h-4 w-4 text-[#42b1c2]" />
                  </div>
                  <span className="text-gray-900">Video candidate profiles and detailed analytics</span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#42b1c2]/10 flex items-center justify-center mt-0.5">
                    <CheckCircle className="h-4 w-4 text-[#42b1c2]" />
                  </div>
                  <span className="text-gray-900">Complete relocation assistance</span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#42b1c2]/10 flex items-center justify-center mt-0.5">
                    <CheckCircle className="h-4 w-4 text-[#42b1c2]" />
                  </div>
                  <span className="text-gray-900">All-in-one recruitment platform</span>
                </div>
              </div>
              
              <Link to="/register/employer">
                <Button size="lg" className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white px-8" data-testid="employer-value-cta">
                  Register as an Employer
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            {/* Right Column - Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="text-3xl font-bold text-[#42b1c2] mb-1">21+</div>
                    <div className="text-sm text-gray-600">Days to first hire*</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="text-3xl font-bold text-[#42b1c2] mb-1">AI</div>
                    <div className="text-sm text-gray-600">Powered screening</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="text-3xl font-bold text-[#42b1c2] mb-1">1</div>
                    <div className="text-sm text-gray-600">Unified platform</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="text-3xl font-bold text-[#42b1c2] mb-1">Full</div>
                    <div className="text-sm text-gray-600">Relocation support</div>
                  </div>
                </div>
                
                {/* Disclaimer */}
                <p className="text-xs text-gray-500 mt-6 text-center">
                  *Timelines vary based on role complexity, documentation readiness, and regulatory requirements.
                </p>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#42b1c2]/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#edd982]/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#42b1c2] to-[#3a9fb0] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Join our growing network of medical professionals and healthcare employers.
          </p>
          <Link to="/join-talent-pool">
            <Button size="lg" className="bg-white text-[#42b1c2] hover:bg-gray-100 px-8 py-6 text-lg">
              Upload Your CV
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Connect with Luma Med Global Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Connect with Luma Med Global
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
              Stay connected with Luma Med Global for updates, insights, and announcements as we 
              continue building our medically led healthcare recruitment platform.
            </p>
            <p className="text-gray-500">
              You can also join our waitlist for early access as the platform rolls out in phases.
            </p>
          </div>

          {/* Social Media Links */}
          <div className="flex justify-center items-center gap-6 mb-12">
            <a 
              href="https://www.linkedin.com/company/lumamedglobal" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-md hover:shadow-lg hover:scale-110 transition-all border border-gray-200 group"
              aria-label="LinkedIn"
              data-testid="social-linkedin"
            >
              <svg className="w-6 h-6 text-[#0077B5] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            
            <a 
              href="https://x.com/lumamedglobal" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-md hover:shadow-lg hover:scale-110 transition-all border border-gray-200 group"
              aria-label="X (Twitter)"
              data-testid="social-x"
            >
              <svg className="w-5 h-5 text-black group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            
            <a 
              href="https://www.tiktok.com/@lumamedglobal" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-md hover:shadow-lg hover:scale-110 transition-all border border-gray-200 group"
              aria-label="TikTok"
              data-testid="social-tiktok"
            >
              <svg className="w-6 h-6 text-black group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
            </a>
          </div>

          {/* Waitlist CTA */}
          <Card className="border-2 border-[#42b1c2]/30 bg-gradient-to-br from-white to-[#42b1c2]/5 max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Join the Luma Med Global Waitlist
              </h3>
              <p className="text-gray-600 mb-6">
                Be among the first medical professionals and healthcare organisations to access Luma Med Global.
              </p>
              <a 
                href="https://waitlist.lumamedglobal.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="waitlist-cta"
              >
                <Button size="lg" className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white px-8 py-6 text-lg">
                  Join the Waitlist
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <p className="text-sm text-gray-500 mt-4">
                Early access will be offered to a limited number of medical professionals and healthcare employers.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Homepage;
